import React, { useEffect, useState } from "react";

import {
  CalendarDays,
  FileText,
  Link2,
  Settings2,
  Shuffle,
} from "lucide-react";

import TabsComponent from "../../Components/Tab";
import NavLinksRegistryTab from "../../Pages/GuidanceLinksAdmin/NavLinksRegistryTab";
import OrgSettingsTab from "../../Pages/GuidanceLinksAdmin/OrgSettingsTab";
import PlaybookContentTab from "../../Pages/GuidanceLinksAdmin/PlaybookContentTab";
import PlaybookLinkAssignmentTab from "../../Pages/GuidanceLinksAdmin/PlaybookLinkAssignmentTab";
import RhythmContentTab from "../../Pages/GuidanceLinksAdmin/RhythmContentTab";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchGuidanceCadenceSchedule,
  fetchGuidanceHubEnabled,
  fetchGuidanceNavLinks,
  fetchGuidancePlaybookLinks,
  fetchGuidanceRhythm,
} from "../../Store/Reducers/GuidanceSlice";

type AdminTab =
  | "hubsettings"
  | "registry"
  | "assignment"
  | "content"
  | "rhythm";

const TAB_ICON_CLASS = "h-4 w-4";

/**
 * IzbaAdmin Config → Playbook Hub — platform-wide settings + content.
 */
const AdminGuidanceHubContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [activeTab, setActiveTab] = useState<AdminTab>("hubsettings");
  const { hubEnabled, navLinksError, playbookLinksError, rhythmError } =
    useAppSelector(state => state.guidance);
  const loadError = navLinksError || playbookLinksError || rhythmError;

  useEffect(() => {
    dispatch(fetchGuidanceHubEnabled());
    dispatch(fetchGuidanceCadenceSchedule());
    dispatch(fetchGuidanceNavLinks());
    dispatch(fetchGuidancePlaybookLinks());
    dispatch(fetchGuidanceRhythm());
  }, [dispatch]);

  return (
    <div className="guidance-hub-admin-page">
      {!hubEnabled && (
        <div className="flex justify-end mb-3">
          <span className="text-[11px] font-bold tracking-wide bg-red-100 text-red-700 rounded px-2 py-0.5">
            Playbooks Hub: OFF
          </span>
        </div>
      )}

      {loadError && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-xs font-semibold text-red-700">
          <span>
            Couldn&apos;t load Guidance Hub admin data: {loadError}. Try
            reloading the page.
          </span>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="shrink-0 rounded-md border border-red-300 bg-white px-2.5 py-1 text-[11px] font-bold text-red-700 hover:bg-red-100"
          >
            Reload
          </button>
        </div>
      )}

      <TabsComponent
        tabs={[
          {
            id: "hubsettings",
            label: "Hub Settings",
            icon: <Settings2 className={TAB_ICON_CLASS} aria-hidden />,
          },
          {
            id: "registry",
            label: "Nav Links Registry",
            icon: <Link2 className={TAB_ICON_CLASS} aria-hidden />,
          },
          {
            id: "assignment",
            label: "Playbook Link Assignment",
            icon: <Shuffle className={TAB_ICON_CLASS} aria-hidden />,
          },
          {
            id: "content",
            label: "Playbook Content",
            icon: <FileText className={TAB_ICON_CLASS} aria-hidden />,
          },
          {
            id: "rhythm",
            label: "Rhythm & Outcomes",
            icon: <CalendarDays className={TAB_ICON_CLASS} aria-hidden />,
          },
        ]}
        activeTab={activeTab}
        variant="secondary-segmented"
        segmentedLayout="fit"
        onTabChange={(id: AdminTab) => setActiveTab(id)}
        className="mb-6 rounded-full"
      />

      {activeTab === "hubsettings" && <OrgSettingsTab />}
      {activeTab === "registry" && <NavLinksRegistryTab />}
      {activeTab === "assignment" && <PlaybookLinkAssignmentTab />}
      {activeTab === "content" && <PlaybookContentTab />}
      {activeTab === "rhythm" && <RhythmContentTab />}
    </div>
  );
};

export default AdminGuidanceHubContainer;
